import { AlertCircle, Mic } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { CardGrid } from "components/CardGrid";
import { SectionHeader } from "components/SectionHeader";
import { EmptyState, LoadingCards } from "components/states";
import { SECTION_TITLES } from "../../copy/section-titles";

import { toDate } from "./agenda-schedule";
import { EventPersonSheet } from "./EventPersonSheet";
import { initials } from "./initials";
import type { AgendaItem, Speaker } from "./schemas";
import { sortSpeakers } from "./speaker-order";
import { useLiveAgenda, useSpeakers } from "./use-event-content";
import { formatSessionStart } from "./use-presenting-sessions";

/** Start of the earliest session each presenter is listed on. */
type FirstSessions = ReadonlyMap<string, Date>;

const NO_SESSIONS: FirstSessions = new Map();

/**
 * The categorical fills for the tile of a speaker without a photo: the Dusk
 * gradients, all dark enough for white initials in either theme.
 */
const TILE_FILLS = [
  "bg-dusk-tile-a text-white",
  "bg-dusk-tile-b text-white",
  "bg-dusk-chapter-2 text-white",
] as const;

/** The same ring every whole-card button in the app carries. */
const CARD_FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

/**
 * `agendaEnabled` is the event's agenda module. The first-session lines come
 * from the agenda, so with it off the agenda is not read at all and the cards
 * simply carry no session.
 */
export function SpeakersSection({
  eventId,
  timeZone,
  agendaEnabled = false,
}: {
  eventId: string;
  /** The event's IANA zone, so a session reads on the clock the agenda shows. */
  timeZone?: string;
  agendaEnabled?: boolean;
}) {
  return agendaEnabled ? (
    <SpeakersWithAgenda eventId={eventId} timeZone={timeZone} />
  ) : (
    <SpeakersView
      eventId={eventId}
      timeZone={timeZone}
      firstSessions={NO_SESSIONS}
    />
  );
}

/** Split off so the agenda is subscribed to only while the module is on. */
function SpeakersWithAgenda({
  eventId,
  timeZone,
}: {
  eventId: string;
  timeZone?: string;
}) {
  const agenda = useLiveAgenda(eventId);
  const firstSessions = useMemo(
    () =>
      agenda.status === "success"
        ? firstSessionByPresenter(agenda.data)
        : NO_SESSIONS,
    [agenda],
  );
  return (
    <SpeakersView
      eventId={eventId}
      timeZone={timeZone}
      firstSessions={firstSessions}
    />
  );
}

/** Keys of `FirstSessions`: a presenter is a speaker or a roster row. */
const speakerKey = (id: string) => `speaker:${id}`;
const attendeeKey = (id: string) => `attendee:${id}`;

/**
 * Read from `presenters`, which the trigger resolves from `presenterIds` and
 * which is the only presenter data the agenda renders. The raw ids also hold
 * ones it left off (a withdrawn attendee, an id matching nobody), so using
 * them would give a chip for a session the Agenda tab does not list the
 * person on.
 *
 * Items with no start time are placeholders the organiser has not finished,
 * which attendees never see on the agenda, so they are not a speaker's first
 * session either.
 */
function firstSessionByPresenter(items: AgendaItem[]): FirstSessions {
  const first = new Map<string, Date>();
  for (const item of items) {
    const start = toDate(item.startsAt);
    if (!start) continue;
    for (const presenter of item.presenters ?? []) {
      const key = presenter.speakerId
        ? speakerKey(presenter.speakerId)
        : presenter.attendeeId
          ? attendeeKey(presenter.attendeeId)
          : null;
      if (!key) continue;
      const earliest = first.get(key);
      if (!earliest || start < earliest) first.set(key, start);
    }
  }
  return first;
}

/**
 * A speaker is listed on a session by their own id, or by the roster id of
 * the attendee they are the same person as (`attendeeId`), whichever the
 * organiser picked. The earlier of the two is the first session.
 */
function firstSessionOf(
  speaker: Speaker,
  firstSessions: FirstSessions,
): Date | undefined {
  const own = firstSessions.get(speakerKey(speaker.id));
  const linked = speaker.attendeeId
    ? firstSessions.get(attendeeKey(speaker.attendeeId))
    : undefined;
  if (own && linked) return own < linked ? own : linked;
  return own ?? linked;
}

function SpeakersView({
  eventId,
  timeZone,
  firstSessions,
}: {
  eventId: string;
  timeZone?: string;
  firstSessions: FirstSessions;
}) {
  const speakers = useSpeakers(eventId);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const count = speakers.status === "success" ? speakers.data.length : 0;
  const head = (
    <SectionHeader
      className="mb-6"
      kicker={SECTION_TITLES.speakers.kicker}
      title={SECTION_TITLES.speakers.title}
      subline={
        count > 0
          ? `${count} ${count === 1 ? "speaker" : "speakers"}.`
          : undefined
      }
    />
  );

  if (speakers.status === "loading")
    return (
      <Frame>
        {head}
        <LoadingCards cards={3} mode="grid" />
      </Frame>
    );
  if (speakers.status === "error")
    return (
      <Frame>
        {head}
        <EmptyState icon={AlertCircle} title="Could not load speakers">
          {speakers.error}
        </EmptyState>
      </Frame>
    );
  if (speakers.data.length === 0)
    return (
      <Frame>
        {head}
        <EmptyState icon={Mic} title="No speakers yet">
          Speakers will appear here once added.
        </EmptyState>
      </Frame>
    );

  const [featured, ...others] = sortSpeakers(speakers.data);
  // The card's own name and picture, should the entry go before the sheet
  // has read it.
  const selected = speakers.data.find((speaker) => speaker.id === selectedId);
  const selectedFallback = selected
    ? { name: selected.name, avatar: selected.avatar }
    : undefined;

  const sessionLine = (speaker: Speaker) => {
    const start = firstSessionOf(speaker, firstSessions);
    return start ? formatSessionStart(start, timeZone) : "";
  };

  return (
    <Frame>
      {head}
      <div className="space-y-3" data-testid="speakers-content">
        <FeatureSpeakerCard
          speaker={featured}
          session={sessionLine(featured)}
          onSelect={() => setSelectedId(featured.id)}
        />
        {others.length > 0 && (
          // A single column below 340px, where two portraits and their gap
          // leave each name about 130px, then two, and more on a desktop.
          <CardGrid
            testId="speakers-grid"
            className="gap-3 min-[340px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
          >
            {others.map((speaker, index) => (
              <SpeakerCard
                key={speaker.id}
                speaker={speaker}
                fill={TILE_FILLS[index % TILE_FILLS.length]}
                session={sessionLine(speaker)}
                onSelect={() => setSelectedId(speaker.id)}
              />
            ))}
          </CardGrid>
        )}
      </div>
      <EventPersonSheet
        eventId={eventId}
        person={selectedId ? { speakerId: selectedId } : null}
        fallback={selectedFallback}
        onClose={() => setSelectedId(null)}
      />
    </Frame>
  );
}

/** The tab's column: capped on a desktop, so the portraits keep their shape. */
function Frame({ children }: { children: ReactNode }) {
  return <div className="mx-auto w-full max-w-5xl">{children}</div>;
}

function roleLine(speaker: Speaker): string {
  return [speaker.role, speaker.company].filter(Boolean).join(", ");
}

/**
 * The first speaker, large: photo under a dark scrim with the name set over
 * it. White text is on the scrim, not on the photo, so it holds on any image.
 */
function FeatureSpeakerCard({
  speaker,
  session,
  onSelect,
}: {
  speaker: Speaker;
  session: string;
  onSelect: () => void;
}) {
  const role = roleLine(speaker);
  return (
    <button
      type="button"
      onClick={onSelect}
      // See SpeakerCard: the name stands in for the whole card.
      aria-label={`${speaker.name}, view profile`}
      className={`relative flex min-h-[26rem] w-full min-w-0 flex-col justify-end overflow-hidden rounded-[28px] border border-border p-5 text-left wrap-anywhere dark:border-edge dark:border-lit-edge dark:shadow-lit sm:min-h-[30rem] sm:p-8 ${CARD_FOCUS}`}
      data-testid={`speaker-card-${speaker.id}`}
      data-featured="true"
    >
      {speaker.avatar ? (
        <img
          src={speaker.avatar}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <>
          <span
            className="absolute inset-0 bg-dusk-chapter-1"
            data-fallback="gradient"
          />
          <span
            aria-hidden="true"
            className="absolute inset-x-0 top-[14%] text-center font-display text-[8rem] leading-none text-white/25"
          >
            {initials(speaker.name)}
          </span>
        </>
      )}
      <span
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-transparent from-35% to-hb-warm-grey/85"
      />
      <span className="relative flex min-w-0 flex-col items-start gap-2">
        {session && (
          <span
            className="rounded-full bg-hb-yellow px-3 py-1 text-[0.8125rem] font-bold uppercase tracking-[0.08em] text-hb-on-yellow"
            data-testid="speaker-first-session"
          >
            {session}
          </span>
        )}
        <span className="font-display text-[2rem] leading-[1.02] text-white sm:text-5xl">
          {speaker.name}
        </span>
        {role && (
          <span className="text-[1.0625rem] text-hb-light-grey">{role}</span>
        )}
      </span>
    </button>
  );
}

function SpeakerCard({
  speaker,
  fill,
  session,
  onSelect,
}: {
  speaker: Speaker;
  /** The categorical fill of the tile shown when there is no photo. */
  fill: string;
  session: string;
  onSelect: () => void;
}) {
  const role = roleLine(speaker);
  return (
    <button
      type="button"
      onClick={onSelect}
      // The button wraps the whole card, and firestore.rules bounds a bio at
      // 10000 characters. Without an explicit name, tabbing the grid reads
      // everything inside the card before it can be activated.
      aria-label={`${speaker.name}, view profile`}
      // `w-full min-w-0`: a button is an inline-block sized to max-content, so
      // without these the grid track widens to fit the longest thing on the
      // card, and one unbreakable name is enough to push the page sideways.
      className={`flex w-full min-w-0 flex-col gap-2.5 rounded-[22px] text-left wrap-anywhere ${CARD_FOCUS}`}
      data-testid={`speaker-card-${speaker.id}`}
    >
      {speaker.avatar ? (
        <img
          src={speaker.avatar}
          alt=""
          loading="lazy"
          className="aspect-square w-full rounded-[22px] object-cover"
        />
      ) : (
        <span
          aria-hidden="true"
          className={`flex aspect-square w-full items-center justify-center rounded-[22px] font-display text-[3.5rem] ${fill}`}
          data-fallback="tile"
        >
          {initials(speaker.name)}
        </span>
      )}
      <span className="flex min-w-0 flex-col gap-0.5 px-0.5 pb-2.5">
        <span className="font-display text-[1.3125rem] leading-[1.12] text-foreground">
          {speaker.name}
        </span>
        {role && <span className="text-sm text-muted-foreground">{role}</span>}
        {session && (
          <span
            className="pt-0.5 text-[0.8125rem] font-semibold text-lit"
            data-testid="speaker-first-session"
          >
            {session}
          </span>
        )}
      </span>
    </button>
  );
}
